import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  compatConfig,
  computeCompatibility,
  type CompatibilityLevel,
  type TagOptionRef,
} from "@/lib/compatibility";
import { api } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { Music2, X } from "lucide-react";

interface ChallengeVariantData {
  languageCode: string;
  localizedTitle: string;
  audioContentId: string | null;
  audioFileUrl?: string | null;
  audioFileName: string | null;
  durationSeconds: number | null;
  thumbnailUrl: string | null;
}

interface ChallengeVariantFieldsProps {
  variant: ChallengeVariantData;
  onFieldChange: (field: keyof ChallengeVariantData, value: any) => void;
  isReadOnly?: boolean;
  currentCategoryId?: string | null;
  currentTagOptionIds?: string[];
  allTagOptions?: TagOptionRef[];
}

interface AudioPickerItem {
  audioContentId: string;
  audioVersionId: string;
  localizedTitle: string;
  durationSeconds: number | null;
  thumbnailUrl: string | null;
  sourceCategoryId: string | null;
  sourceTagOptionIds: string[];
}

function formatDurationLabel(seconds: number | null): string | null {
  if (!seconds || seconds <= 0) return null;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function ChallengeVariantFields({
  variant,
  onFieldChange,
  isReadOnly,
  currentCategoryId,
  currentTagOptionIds = [],
  allTagOptions = [],
}: ChallengeVariantFieldsProps) {
  const languageCode = variant.languageCode || "en";
  const { data: audioLibrary = [], isLoading: isLoadingAudio } = useQuery<
    AudioPickerItem[]
  >({
    queryKey: ["/audio/picker", { languageCode }],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set("languageCode", languageCode);
      return api.get<AudioPickerItem[]>(`/audio/picker?${params.toString()}`);
    },
  });

  const hasTargeting =
    !!currentCategoryId || (currentTagOptionIds?.length ?? 0) > 0;

  const getCompatibility = (item: AudioPickerItem): CompatibilityLevel =>
    computeCompatibility(
      item.sourceTagOptionIds ?? [],
      currentTagOptionIds ?? [],
      item.sourceCategoryId ?? null,
      currentCategoryId ?? null,
      allTagOptions,
    );

  const hasLegacyAudio = !variant.audioContentId && !!variant.audioFileUrl;
  const selectedAudio = variant.audioContentId
    ? audioLibrary.find((a) => a.audioContentId === variant.audioContentId) ||
      ({
        audioContentId: variant.audioContentId,
        audioVersionId: "",
        localizedTitle: variant.audioFileName || "Selected audio",
        durationSeconds: variant.durationSeconds,
        thumbnailUrl: variant.thumbnailUrl,
        sourceCategoryId: null,
        sourceTagOptionIds: [],
      } as AudioPickerItem)
    : hasLegacyAudio
      ? ({
          audioContentId: "",
          audioVersionId: "",
          localizedTitle:
            variant.audioFileName || "Legacy audio (not linked to library)",
          durationSeconds: variant.durationSeconds,
          thumbnailUrl: variant.thumbnailUrl,
          sourceCategoryId: null,
          sourceTagOptionIds: [],
        } as AudioPickerItem)
      : null;

  const selectedCompat: CompatibilityLevel | null =
    selectedAudio && hasTargeting ? getCompatibility(selectedAudio) : null;

  const handleAudioSelect = (audioContentId: string) => {
    if (audioContentId === "__none__") {
      onFieldChange("audioContentId", null);
      onFieldChange("audioFileName", null);
      onFieldChange("durationSeconds", null);
      onFieldChange("thumbnailUrl", null);
      return;
    }
    const item = audioLibrary.find((a) => a.audioContentId === audioContentId);
    if (!item) return;
    onFieldChange("audioContentId", item.audioContentId);
    onFieldChange("audioFileName", item.localizedTitle);
    onFieldChange("durationSeconds", item.durationSeconds);
    onFieldChange("thumbnailUrl", item.thumbnailUrl);
  };

  const clearAudio = () => {
    onFieldChange("audioContentId", null);
    onFieldChange("audioFileUrl", null);
    onFieldChange("audioFileName", null);
    onFieldChange("durationSeconds", null);
    onFieldChange("thumbnailUrl", null);
  };

  return (
    <div className="space-y-4 pt-4">
      <div className="space-y-2">
        <Label>Title *</Label>
        <Input
          value={variant.localizedTitle}
          onChange={(e) => onFieldChange("localizedTitle", e.target.value)}
          placeholder="Enter challenge title"
          disabled={isReadOnly}
          data-testid="input-title"
        />
      </div>

      <div className="space-y-2">
        <Label>Audio</Label>
        {selectedAudio ? (
          <div
            className="flex items-center gap-3 rounded-md border bg-muted/30 px-3 py-3"
            data-testid="audio-selected-preview"
          >
            <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center shrink-0">
              <Music2 className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">
                {selectedAudio.localizedTitle}
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                {formatDurationLabel(selectedAudio.durationSeconds) && (
                  <span className="text-xs text-muted-foreground">
                    {formatDurationLabel(selectedAudio.durationSeconds)}
                  </span>
                )}
                {hasLegacyAudio && (
                  <span
                    className="text-xs text-amber-600 dark:text-amber-400"
                    data-testid="badge-legacy-audio"
                  >
                    Legacy URL
                  </span>
                )}
                {selectedCompat && (
                  <CompatBadge
                    level={selectedCompat}
                    testId="badge-selected-audio-compat"
                  />
                )}
              </div>
            </div>
            {!isReadOnly && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={clearAudio}
                data-testid="button-clear-audio"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        ) : (
          <Select
            value={undefined}
            onValueChange={handleAudioSelect}
            disabled={isReadOnly || isLoadingAudio}
          >
            <SelectTrigger data-testid="select-audio-file">
              <SelectValue
                placeholder={
                  isLoadingAudio
                    ? "Loading audio library..."
                    : audioLibrary.length === 0
                      ? `No published audio in ${languageCode.toUpperCase()}`
                      : "Choose audio from library"
                }
              />
            </SelectTrigger>
            <SelectContent>
              {audioLibrary.length === 0 ? (
                <div className="px-2 py-3 text-xs text-muted-foreground">
                  No published audio in {languageCode.toUpperCase()}. Publish an
                  audio in this language first.
                </div>
              ) : (
                audioLibrary.map((a) => {
                  const dur = formatDurationLabel(a.durationSeconds);
                  const compat = hasTargeting ? getCompatibility(a) : null;
                  return (
                    <SelectItem
                      key={a.audioContentId}
                      value={a.audioContentId}
                      data-testid={`option-audio-${a.audioContentId}`}
                    >
                      <div className="flex items-center justify-between gap-3 w-full">
                        <span className="truncate">
                          {a.localizedTitle}
                          {dur ? ` · ${dur}` : ""}
                        </span>
                        {compat && (
                          <CompatBadge
                            level={compat}
                            testId={`badge-audio-compat-${a.audioContentId}`}
                          />
                        )}
                      </div>
                    </SelectItem>
                  );
                })
              )}
            </SelectContent>
          </Select>
        )}
        {hasTargeting && audioLibrary.length > 0 && !selectedAudio && (
          <p className="text-xs text-muted-foreground">
            Compatibility scored on category + tag overlap.
          </p>
        )}
      </div>

    </div>
  );
}

function CompatBadge({
  level,
  testId,
}: {
  level: CompatibilityLevel;
  testId?: string;
}) {
  const cfg = compatConfig[level];
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs ${cfg.color}`}
      data-testid={testId}
    >
      <Icon className="h-3 w-3" />
      {cfg.label}
    </span>
  );
}
